import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { WorkViewerInfo, WorkViewerTitle } from '../../../shared/component/headless/input/InputBody';
import { useTextViewStore } from '../../../shared/store/TextViewStore';
import { useTextListActions } from '../../../shared/hooks/useApi/useTextList';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { AlertType, AlertPosition, ModalType } from '../../../shared/enum/EnumRepository';
import { useValidation } from '../../../shared/hooks/useValidation';
import { UpdateTextBlockReq } from '../../../shared/dto/ReqDtoRepository';
import { TextBlockType } from '../../../shared/enum/EnumRepository';
import { convertTextBlockToUpdateReq } from '../../../shared/converter/converter';
import TextBlockListFixed from './TextBlockListFixed';
import MoleculeInputDiv from '../../../shared/component/molecule/MoleculeInputDiv';
import { useTextBlock } from '../../../shared/hooks/useApi/useTextBlock';
import createIcon from '../../../asset/icon/create.png';
import reorderIcon from '../../../asset/icon/reorder.png';
import HintWrapper from '../../../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../../../shared/constants/hintMessages';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import MoleculeDivToggle from '../../../shared/component/molecule/MoleculeDivToggle';
import { useImage } from '../../../shared/hooks/useApi/useImage';
import deleteOpenIcon from '../../../asset/icon/delete_open.png';
import deleteCloseIcon from '../../../asset/icon/delete_close.png';
import confirmIcon from '../../../asset/icon/confirm2.png';
import confirmIcon2 from '../../../asset/icon/confirm3.png';
import { INPUT_YEAR_MIN_LENGTH, INPUT_YEAR_MAX_LENGTH } from '../../../shared/constants/limits';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import RelatedProjectList from '../RelatedProjectListInText';
import { toUpdateTextReq } from '../../../shared/util/textTag';


const TextViewer: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { isOwner } = useAuth();
  const activeText = useTextViewStore((s) => s.activeText);
  const textHasChanged = useTextViewStore((s) => s.hasChanged);
  const activeTextBlockList = useTextViewStore((s) => s.activeTextBlockList);
  const handleChange = useTextViewStore((s) => s.updateActiveText);
  const { toggleTextAccess, updateText, deleteText } = useTextListActions();
  const { createTextBlock } = useTextBlock();
  const { enqueueAlert } = useStandardAlertStore();
  const { checkType, isValidTextBlockLinkUrl } = useValidation();
  const { setStandardModal } = useModalStore();
  const { uploadTextBlockImage } = useImage();

  const hasChanged = textHasChanged || activeTextBlockList.some(tb => tb.hasChanged);

  if (!activeText) return null;

  const handleChangeWithValidate = (
    field: 'title' | 'subtitle' | 'prodYear' | 'author',
    value: string
  ) => {
    if (field === 'author') {
      handleChange({
        source: { ...activeText.source, author: value as string }
      });
    }
    if (field === 'prodYear' && !checkType(field, value as string)) {
      return;
    };
    handleChange({ [field]: value });
  }

  const handleIsPublicToggle = async (value: boolean) => {
    if (!activeText) return;
    if (!isEditMode) return;

    await toggleTextAccess(
      toUpdateTextReq({ ...activeText, isPublic: value }, { includeTagIds: false }),
    );
  };

  const handleConfirm = async () => {
    if (!activeText) return;

    if (activeTextBlockList.some((tb) =>
      tb.type === TextBlockType.LINK && !isValidTextBlockLinkUrl(tb.url || '')
    )) {
      return;
    }

    const changedEntries = activeTextBlockList
      .map((tb, index) => ({ block: tb, index }))
      .filter(({ block }) => block.hasChanged);

    const finalizedTextBlocks: UpdateTextBlockReq[] = [];

    for (const { block, index } of changedEntries) {
      const req = convertTextBlockToUpdateReq(block, index);

      let nextReq = req;
      if (
        block.type === TextBlockType.IMAGE &&
        block.imageChanged
      ) {
        const uploaded = await uploadTextBlockImage(activeText.id, req);
        if (!uploaded) return;
        nextReq = uploaded;
      }

      finalizedTextBlocks.push(nextReq);
    }

    await updateText(
      toUpdateTextReq(activeText, {
        textBlocks: finalizedTextBlocks.length > 0 ? finalizedTextBlocks : undefined,
      }),
    );
  };

  const handleDelete = async () => {
    const callback = async () => {
      await deleteText({ textId: activeText.id });
    }
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_TEXT'),
      callBack: callback
    });
  };

  const handleAddTextBlock = async () => {
    await createTextBlock({
      textId: activeText.id,
      type: TextBlockType.PARAGRAPH,
    });
  };

  const handleReOrderTextBlock = () => {
    setStandardModal({
      modalType: ModalType.INDEXING,
      title: "Text",
      value: null,
      handleChange: () => { },
    });
  };

  return (
    <>
      <TextInfoContainer>
        <MoleculeInputDiv
          value={activeText.title}
          defaultValue={"Title"}
          DefaultDiv={TitleDefaultDiv}
          placeholder={"Title"}
          handleChange={(e) => handleChangeWithValidate("title", e.target.value)}
          inputStyle={WorkViewerTitle}
          StyledDiv={Title}
        />
        <TextInfo>
          {(activeText.subtitle || isEditMode ) &&
          <TextInfoRow>
            <TextInfoLabel>Subtitle</TextInfoLabel>
            <MoleculeInputDiv
              value={activeText.subtitle || ""}
              defaultValue={"Subtitle"}
              DefaultDiv={DefaultDiv}
              placeholder={"Subtitle"}
              handleChange={(e) => handleChangeWithValidate("subtitle", e.target.value)}
              inputStyle={WorkViewerInfo}
              StyledDiv={Info}
              />
          </TextInfoRow>
          }
          <TextInfoRow>
            <TextInfoLabel>Author</TextInfoLabel>
            <MoleculeInputDiv
              value={activeText.source.author}
              defaultValue={"Author"}
              DefaultDiv={DefaultDiv}
              placeholder={"Author"}
              handleChange={(e) => handleChangeWithValidate("author", e.target.value)}
              inputStyle={WorkViewerInfo}
              StyledDiv={Info}
              />
          </TextInfoRow>
          <TextInfoRow>
            <TextInfoLabel>Year</TextInfoLabel>
            <MoleculeInputDiv
              value={activeText.prodYear}
              defaultValue={"Year"}
              DefaultDiv={DefaultDiv}
              placeholder={"Year"}
              handleChange={(e) => handleChangeWithValidate("prodYear", e.target.value)}
              inputStyle={WorkViewerInfo}
              StyledDiv={Info}
              minLimit={INPUT_YEAR_MIN_LENGTH}
              maxLimit={INPUT_YEAR_MAX_LENGTH}
              />
          </TextInfoRow>
        </TextInfo>
        {isEditMode && isOwner && (
          <TextAccessToggles>
            <MoleculeDivToggle
              value={activeText.isPublic}
              name={activeText.isPublic ? 'Public' : 'Private'}
              handleToggle={(e) => handleIsPublicToggle(e.target.checked)}
            />
          </TextAccessToggles>
        )}
      </TextInfoContainer>
      {isEditMode &&
        <BtnContainer
          data-has-changed={hasChanged ? "true" : "false"}
        >
          <HintWrapper content={getHintMessage('ADD_TEXT_BLOCK')} toLeft={true}>
            <UniversalIconBtn
              size="fit"
              border={false}
              backgroundImg={createIcon}
              onClick={handleAddTextBlock} />
          </HintWrapper>
          <HintWrapper content={getHintMessage('REORDER_TEXT_BLOCKS')} toLeft={true}>
            <UniversalIconBtn
              size="fit"
              border={false}
              backgroundImg={reorderIcon}
              onClick={handleReOrderTextBlock} />
          </HintWrapper>
          {hasChanged && (
            <HintWrapper content={getHintMessage('UPDATE_TEXT')} toLeft={true}>
              <UniversalIconBtn
                isPrimary={true}
                size="fit"
                border={false}
                backgroundImg={confirmIcon}
                backgroundImgHover={confirmIcon2}
                onClick={handleConfirm} />
            </HintWrapper>
          )}
          <HintWrapper content={getHintMessage('DELETE_TEXT')} toLeft={true}>
            <UniversalIconBtn
              size="fit"
              border={false}
              backgroundImg={deleteCloseIcon}
              backgroundImgHover={deleteOpenIcon}
              onClick={handleDelete} />
          </HintWrapper>
        </BtnContainer>
      }
      <TextBlockListFixed />
      <RelatedProjectList />
    </>
  );
}


const TextInfoContainer = styled.div`
  display: flex;
  flex-direction: column;

  padding: 0;

  gap: 8px;
`

const Title = styled.h2`
  width: 100%;
  min-height: 28px;

  display: flex;
  align-items: center;

  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const TitleDefaultDiv = styled.div`
  width: 100%;

  display: flex;
  align-items: center;

  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_02_1};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const TextInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const Info = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.2;
`;

const DefaultDiv = styled.div`
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_04};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const TextInfoRow = styled.div`
  height: 18px;
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const TextInfoLabel = styled.span`
  min-width: 70px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const BtnContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 10px;
  gap: 0.5vw;
`;

const TextAccessToggles = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0 4px;
`;

export default TextViewer;
